/**
 * GraphQL fragments and queries for CardV2 component.
 * Uses the new convenience fields from the GraphQL schema.
 */

import { gql } from '@apollo/client';

/**
 * Core fields for CardV2 component.
 * Uses convenience fields for simplified data access.
 */
export const CARD_V2_CORE_FIELDS = gql`
  fragment CardV2CoreFields on CardSchema {
    id
    cardId
    type
    title
    description
    createdAt
    displayThumbnailUrl
    displaySecondaryImageUrl
    displayTitle
    displayCreatorNames
    displayBackgroundColor
    displayFontColor
    displayGradient {
      start
      end
      predefinedName
    }
    mediaTagNames(limit: 3)
    tagNames(limit: 1)
    picksCount
    commentsCount
    primarySourceDomain
    primarySourceUrl
    embedUrl
    itemsCount
    collaboratorsCount
    responsesCount
    sourceName
    externalSources {
      totalCount
      items {
        id
        sourceUrl
        sourceDomainName
      }
    }
  }
`;

/**
 * Owner preview fields for CardV2 header/facepile.
 */
export const CARD_V2_OWNER_FIELDS = gql`
  fragment CardV2OwnerFields on CardSchema {
    ownerPreview {
      id
      username
      displayName
      avatarUrl
      isVerified
    }
    extraData {
      totalCount
      items {
        ... on CardCommentSchema {
          id
          text
          createdAt
          user {
            id
            username
            cardId
            isVerified
            isFollowedByMe
            userInfo {
              firstName
              lastName
              bio
            }
            userStyle {
              id
              thumbnailImageSet {
                id
                imageId
                images {
                  url
                  id
                }
              }
            }
          }
          collection {
            id
            cardId
            type
            title
          }
        }
        ... on CardPickSchema {
          id
          cardId
          createdAt
          user {
            id
            username
            cardId
            isVerified
            isFollowedByMe
            userInfo {
              firstName
              lastName
              bio
            }
            userStyle {
              id
              thumbnailImageSet {
                id
                imageId
                images {
                  url
                  id
                }
              }
            }
          }
        }
        ... on CollectionCardLinkSchema {
          id
          cardPickId
          createdAt
          createdAtStamp
          pickCreatedAt
          user {
            id
            username
            cardId
            isVerified
            isFollowedByMe
            userInfo {
              firstName
              lastName
              bio
            }
            userStyle {
              id
              thumbnailImageSet {
                id
                imageId
                images {
                  url
                  id
                }
              }
            }
          }
          collection {
            id
            type
            title
            cardId
          }
        }
      }
    }
  }
`;

/**
 * Canonical info fields for multi-source content.
 * Provides consistent display data across linked cards.
 */
export const CARD_V2_CANONICAL_FIELDS = gql`
  fragment CardV2CanonicalFields on CardSchema {
    canonicalInfo {
      displayTitle
      displayImageUrl
      displayCreatorNames
      matchType
      isAuthoritySource
      allSources {
        sourceName
        sourceDomain
        externalLink
        cardId
        userId
        isPrimary
      }
    }
  }
`;

/**
 * External creators fields for artist/author information.
 */
export const CARD_V2_CREATORS_FIELDS = gql`
  fragment CardV2CreatorsFields on CardSchema {
    externalCreators(limit: 3) {
      totalCount
      items {
        id
        creatorName
        creatorUsername
      }
    }
  }
`;

/**
 * Complete CardV2 fragment combining all field groups.
 * Use this fragment for full card display in Discovery/Explore feeds.
 */
export const CARD_V2_FIELDS = gql`
  fragment CardV2Fields on CardSchema {
    ...CardV2CoreFields
    ...CardV2OwnerFields
    ...CardV2CanonicalFields
    ...CardV2CreatorsFields
  }
  ${CARD_V2_CORE_FIELDS}
  ${CARD_V2_OWNER_FIELDS}
  ${CARD_V2_CANONICAL_FIELDS}
  ${CARD_V2_CREATORS_FIELDS}
`;

/**
 * Lightweight CardV2 fragment for list views.
 * Excludes canonical info for performance.
 */
export const CARD_V2_LITE_FIELDS = gql`
  fragment CardV2LiteFields on CardSchema {
    ...CardV2CoreFields
    ...CardV2OwnerFields
  }
  ${CARD_V2_CORE_FIELDS}
  ${CARD_V2_OWNER_FIELDS}
`;
